import React, { useMemo, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaChevronRight, FaShoppingCart, FaDoorOpen, FaDownload, FaStore, FaTools, FaChartLine, FaGlobe } from 'react-icons/fa';

// Separate Icon component to reduce re-renders
const ModuleIcon = ({slug}) => {
  switch(slug) {
    case 'ventas': return <FaStore />;
    case 'operaciones': return <FaTools />;
    case 'productividad': return <FaChartLine />;
    case 'web': return <FaGlobe />;
    default: return null;
  }
};

// Pre-defined fallback images to avoid DOM manipulations
const FALLBACK_IMAGE = '/images/erp/erp-general.jpg';

const ProductosOpusErp = () => {
  // Data for modules
  const modules = useMemo(() => [
    {
      id: 1,
      name: "Ventas",
      slug: "ventas",
      image: "/images/erp-ventas.svg",
      bgImage: "/images/erpBAN/erp_ventas.jpg",
      color: "#0055B8",
      shortDesc: "Administración integral de las ventas y relaciones con clientes.",
      description: "Optimiza tus procesos de ventas con un sistema integral que gestiona desde cotizaciones hasta facturación, seguimiento de clientes y análisis de desempeño comercial.",
      features: [
        "CRM completo",
        "Cotizaciones y seguimiento",
        "Facturación electrónica",
        "Reportes de ventas",
        "Gestión de comisiones"
      ]
    },
    {
      id: 2,
      name: "Operaciones",
      slug: "operaciones",
      image: "/images/erp-operaciones.svg",
      bgImage: "/images/erpBAN/erp_op.jpg",
      color: "#EC7700",
      shortDesc: "Control detallado de proyectos, recursos y procesos operativos.",
      description: "Administra todos los aspectos operativos de tus proyectos de construcción, desde planificación y ejecución hasta control de recursos, monitoreo de avances y gestión de subcontratistas.",
      features: [
        "Control de proyectos",
        "Gestión de recursos",
        "Seguimiento de avances",
        "Administración de subcontratistas",
        "Reportes operativos"
      ]
    },
    {
      id: 3,
      name: "Productividad",
      slug: "productividad",
      image: "/images/erp-productividad.svg",
      bgImage: "/images/erpBAN/erp_pro.webp",
      color: "#54B435",
      shortDesc: "Herramientas para optimizar el rendimiento y eficiencia interna.",
      description: "Maximiza la eficiencia de tu empresa con herramientas para gestión documental, comunicación interna, seguimiento de tareas, automatización de procesos y análisis de productividad.",
      features: [
        "Gestión documental",
        "Comunicación interna",
        "Seguimiento de tareas",
        "Automatización de procesos",
        "Análisis de productividad"
      ]
    },
    {
      id: 4,
      name: "Web",
      slug: "web",
      image: "/images/erp-web.svg",
      bgImage: "/images/erpBAN/erp_web.png",
      color: "#6759FF",
      shortDesc: "Presencia digital optimizada para empresas de construcción.",
      description: "Expande tu presencia digital con una plataforma web integrada que incluye sitio web profesional, catálogo de proyectos, portal de clientes, seguimiento de obra en línea y marketing digital.",
      features: [
        "Sitio web profesional",
        "Catálogo de proyectos",
        "Portal de clientes",
        "Seguimiento de obra en línea",
        "Marketing digital"
      ]
    }
  ], []);

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Simplified image error handling using React pattern instead of DOM manipulation
  const handleImageError = (e) => {
    e.target.src = FALLBACK_IMAGE;
    e.target.onerror = null; // Prevent infinite error loops
  };

  // Smooth scroll to section - simplified
  const scrollToModules = (e) => {
    e.preventDefault();
    const modulesSection = document.getElementById('modules');
    if (modulesSection) {
      modulesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Module card component
  const ModuleCard = ({module, index}) => (
    <div className={`flex flex-col ${index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-center`}>
      <div className="w-full md:w-2/5 relative rounded-lg overflow-hidden shadow-lg">
        <div className="relative h-64">
          {/* Background color that shows if image fails */}
          <div className="absolute inset-0" style={{background: `linear-gradient(135deg, ${module.color}, #003171)`}} />
          
          {/* Image with simplified error handling */}
          <img 
            src={module.bgImage} 
            alt={`OPUS ERP ${module.name}`} 
            className="w-full h-full object-cover object-center relative z-10"
            loading="lazy"
            onError={handleImageError}
          />
          
          {/* Overlay for better text visibility */}
          <div className="absolute inset-0 bg-black/40 z-20"></div>
          
          {/* OPUS ERP Logo */}
          <div className="absolute inset-0 flex items-center justify-center z-30">
            <div className="bg-white/90 rounded-lg px-8 py-3 pt-4 shadow-lg">
              <img 
                src="/images/opus-erp-logo.svg" 
                alt="OPUS ERP" 
                className="h-12 w-auto object-contain" 
                loading="lazy"
                onError={(e) => {
                  e.target.src = FALLBACK_IMAGE;
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="w-full md:w-3/5">
        <div className="flex items-center mb-4">
          <div 
            className="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: module.color }}
          >
            <ModuleIcon slug={module.slug} />
          </div>
          <h3 className="text-3xl font-bold text-[#1A2B39]">
            OPUS ERP {module.name}
          </h3>
        </div>
        
        <p className="text-gray-600 text-lg mb-6">{module.description}</p>
        
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-3 text-[#1A2B39]">Características principales:</h4>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4">
            {module.features.map((feature, i) => (
              <li key={i} className="flex items-start">
                <FaCheckCircle className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Link 
            to={`/erp/${module.slug}`}
            className="bg-[#0066CC] text-white px-5 py-2 rounded-md font-medium hover:bg-[#0055AA] transition-colors flex items-center"
          >
            Ver Módulo <FaChevronRight className="ml-2" />
          </Link>
          <Link 
            to="/Distribuidores"
            className="bg-[#EC7700] text-white px-5 py-2 rounded-md font-medium hover:bg-[#D96C00] transition-colors flex items-center"
          >
            Comprar <FaShoppingCart className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );

  // Predefine overview items to reduce render work
  const overviewItems = [
    { 
      icon: <FaStore className="text-[#0066CC] text-2xl" />, 
      title: "Ventas", 
      description: "Gestión comercial, seguimiento de clientes, cotizaciones y facturación en un solo lugar.",
      url: "/erp/ventas"
    },
    { 
      icon: <FaTools className="text-[#0066CC] text-2xl" />, 
      title: "Operaciones", 
      description: "Control integral de proyectos, recursos, avances y gestión de todos los procesos operativos.",
      url: "/erp/operaciones"
    },
    { 
      icon: <FaChartLine className="text-[#0066CC] text-2xl" />, 
      title: "Productividad", 
      description: "Herramientas para optimizar procesos internos, comunicación, documentación y análisis.",
      url: "/erp/productividad"
    },
    { 
      icon: <FaGlobe className="text-[#0066CC] text-2xl" />, 
      title: "Web", 
      description: "Presencia digital, portal de clientes y seguimiento de proyectos en línea.",
      url: "/erp/web"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="OPUS ERP - Gestión Empresarial | ECOSOFT"
        description="Sistema integral de gestión empresarial diseñado específicamente para empresas de construcción: ventas, operaciones, productividad y presencia web."
        name="ProductosOpusErp"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="relative h-[450px] bg-gradient-to-r from-[#1A2B39] to-[#0055B8]">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-4">OPUS ERP</h1>
            <p className="text-xl mb-8">
              Sistema integral de gestión empresarial diseñado específicamente para empresas de construcción.
              Integre ventas, operaciones, productividad y presencia web en una sola plataforma.
            </p>
            <div className="flex gap-4">
              <a 
                href="#modules"
                className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors flex items-center"
                onClick={scrollToModules}
              >
                Explorar Módulos <FaChevronRight className="ml-2" />
              </a>
              <Link 
                to="/Distribuidores"
                className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors flex items-center"
              >
                Solicitar Demostración <FaDoorOpen className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-[#1A2B39]">Gestión Empresarial para Construcción</h2>
            <p className="text-gray-600 text-lg">
              OPUS ERP ha sido desarrollado específicamente para atender las necesidades únicas del sector 
              de la construcción, integrando todas las áreas de su empresa y conectándose perfectamente con 
              los módulos de presupuestación, planeación y control de OPUS.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {overviewItems.map((item, index) => (
              <Link 
                key={index} 
                to={item.url}
                className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-all transform hover:-translate-y-1 group"
              >
                <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0066CC]/20 transition-colors">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-[#1A2B39] group-hover:text-[#0066CC] transition-colors">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.description}</p>
                <span className="inline-flex items-center text-[#0066CC] font-medium">
                  Explorar <FaChevronRight className="ml-2" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* Modules Section */}
      <section id="modules" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#1A2B39]">Módulos OPUS ERP</h2>
          
          <div className="space-y-16">
            {modules.map((module, index) => (
              <ModuleCard key={module.id} module={module} index={index} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Integration Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-[#1A2B39]">Integración con OPUS</h2>
            <p className="text-gray-600 text-lg">
              OPUS ERP se integra perfectamente con los módulos de presupuestos, planeación, 
              compras, dependencias y BIM, creando un ecosistema completo para su empresa constructora.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4 text-[#1A2B39]">Integración con Presupuestos</h3>
              <p className="text-gray-600 mb-4">
                Todos los presupuestos generados en OPUS se sincronizan automáticamente con el ERP, 
                permitiendo su seguimiento financiero y operativo a lo largo de todo el proyecto.
              </p>
              <Link to="/modulos/presupuesto-programable" className="text-[#0066CC] font-medium hover:underline flex items-center">
                Ver M1 - Presupuesto Programable <FaChevronRight className="ml-2" />
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4 text-[#1A2B39]">Integración con Planeación</h3>
              <p className="text-gray-600 mb-4">
                La programación de actividades, recursos y cronogramas se refleja en el ERP 
                para el seguimiento de avances reales contra lo programado.
              </p>
              <Link to="/modulos/planeacion-control" className="text-[#0066CC] font-medium hover:underline flex items-center">
                Ver M2 - Planeación y Control <FaChevronRight className="ml-2" />
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4 text-[#1A2B39]">Integración con Compras</h3>
              <p className="text-gray-600 mb-4">
                Las requisiciones, órdenes de compra y gestión de inventarios se complementan 
                con el control financiero y operativo del ERP.
              </p>
              <Link to="/modulos/administracion-compras" className="text-[#0066CC] font-medium hover:underline flex items-center">
                Ver M3 - Administración de Compras <FaChevronRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para transformar la gestión de su empresa?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Dé el salto al siguiente nivel con OPUS ERP y obtenga control total sobre sus procesos comerciales, 
            operativos y administrativos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/Distribuidores"
              className="px-8 py-3 bg-[#0066CC] hover:bg-[#0088FF] text-white font-medium rounded-md transition-colors flex items-center"
            >
              Adquirir OPUS ERP <FaShoppingCart className="ml-2" />
            </Link>
            <Link 
              to="/Distribuidores"
              className="px-8 py-3 border border-white hover:bg-white hover:text-[#1A2B39] text-white font-medium rounded-md transition-colors"
            >
              Solicitar Demostración
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductosOpusErp; 